// CardDeck: Vertical card-stacking deck engine
(function () {
  'use strict';

  if (window.CardDeck) return;

  // Tunables
  var DURATION = 320;
  var EASING = 'cubic-bezier(0.25, 1, 0.5, 1)';
  var DRAG_COMMIT_RATIO = 0.22;
  var FLICK_VELOCITY = 0.35;
  var WHEEL_DELTA_MIN = 4;
  var WHEEL_TAIL = 90;
  var EDGE_EPSILON = 2;
  var ENGAGE_RATIO = 0.9; // Min viewport coverage ratio to engage gesture capture
  var ALIGN_SLACK = 6;
  var SCROLL_SETTLE = 140;
  var RELEASE_GRACE = 1200;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function now() {
    return (window.performance && performance.now) ? performance.now() : Date.now();
  }

  function clamp(v, min, max) {
    return v < min ? min : (v > max ? max : v);
  }

  // Toggle deck-in-motion class on root for backdrop-filter performance
  function syncMotionFlag() {
    var moving = !!document.querySelector('.deck.is-animating, .deck.is-dragging');
    document.documentElement.classList.toggle('deck-in-motion', moving);
  }

  // CardDeck constructor
  function CardDeck(root, options) {
    if (!root) throw new Error('CardDeck: root element is required');

    this.root = root;
    this.options = options || {};
    this.gestureRoot = this.options.gestureRoot || this.root.closest('product-immersive, image-parallax') || this.root.parentElement || this.root;

    this.slides = Array.prototype.slice.call(
      root.querySelectorAll(this.options.slideSelector || '[data-deck-slide]')
    );

    this.index = 0;
    this.animating = false;
    this.locked = false;
    this.destroyed = false;

    this._wheelBlockedUntil = 0;
    this._wheelGestureActive = false;
    this._wheelDebounceTimer = null;
    this._drag = null;
    this._transitionTimer = null;
    this._active = null;
    // When the deck last handed a gesture back to the page at an edge. While
    // this is recent, scroll-settle alignment stays out of the way so the user
    // can actually leave the section instead of being pulled back in.
    this._releasedAt = 0;
    // Whether the deck owned input the last time we looked. Going false -> true
    // is a *fresh entry*, which is the only moment we may re-seat the stack.
    this._wasEngaged = false;
    this._boundHandlers = [];

    if (!this.slides.length) return;

    this._setupDom();
    this._bind();
    this._commitIndex(0, { silent: true });
  }

  /* ---------- geometry ---------- */

  CardDeck.prototype.slideHeight = function () {
    // The deck is exactly one slide tall, so its own box is the travel distance.
    return this.root.getBoundingClientRect().height || window.innerHeight;
  };

  CardDeck.prototype.isBeginning = function () {
    return this.index <= 0;
  };

  CardDeck.prototype.isEnd = function () {
    return this.index >= this.slides.length - 1;
  };

  // DOM setup
  CardDeck.prototype._setupDom = function () {
    this.root.classList.add('deck');
    // Prevent Android Chrome pull-to-refresh when deck sits at page top
    document.documentElement.classList.add('has-card-deck');
    this.root.setAttribute('role', 'region');
    if (!this.root.hasAttribute('aria-roledescription')) {
      this.root.setAttribute('aria-roledescription', 'carousel');
    }
    if (!this.root.hasAttribute('tabindex')) {
      this.root.setAttribute('tabindex', '0');
    }

    var total = this.slides.length;

    this.slides.forEach(function (slide, i) {
      slide.classList.add('deck__slide');
      slide.style.zIndex = String(i + 1);
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', (i + 1) + ' of ' + total);
      this._applyTransform(slide, i > 0 ? 100 : 0, false);
    }, this);
  };

  // Apply 2D transform to slide (0 = active, 100 = parked below)
  CardDeck.prototype._applyTransform = function (slide, percent, animate) {
    if (animate && !reduceMotion.matches) {
      slide.style.transition = 'transform ' + DURATION + 'ms ' + EASING;
    } else {
      slide.style.transition = 'none';
    }
    slide.style.transform = percent === 0
      ? 'translateY(0)'
      : 'translateY(' + percent + '%)';
  };

  CardDeck.prototype._applyTransformPx = function (slide, px) {
    slide.style.transition = 'none';
    slide.style.transform = 'translateY(' + px + 'px)';
  };

  // State management
  CardDeck.prototype._commitIndex = function (i, opts) {
    opts = opts || {};
    var previous = this.index;
    this.index = i;

    this.slides.forEach(function (slide, n) {
      slide.classList.toggle('is-active', n === i);
      slide.classList.toggle('is-stacked', n < i);
      slide.classList.toggle('is-parked', n > i);
      // Only hide parked slides from assistive tech
      if (n > i) {
        slide.setAttribute('aria-hidden', 'true');
      } else {
        slide.removeAttribute('aria-hidden');
      }
    });

    this.root.dataset.deckIndex = String(i);
    this.root.classList.toggle('is-first', this.isBeginning());
    this.root.classList.toggle('is-last', this.isEnd());

    if (!opts.silent && previous !== i) {
      this.root.dispatchEvent(new CustomEvent('deck:change', {
        detail: { index: i, previousIndex: previous, slide: this.slides[i] },
        bubbles: true
      }));
    }
  };

  // Transition to target slide index
  CardDeck.prototype.goTo = function (target, opts) {
    opts = opts || {};
    if (this.destroyed || !this.slides.length) return;

    target = clamp(target, 0, this.slides.length - 1);
    if (target === this.index && !opts.force) return;
    if (this.animating && !opts.force) return;

    var animate = opts.animate !== false && !reduceMotion.matches;
    var from = this.index;
    var self = this;

    if (!animate) {
      this._cancelTransition();
      this.slides.forEach(function (slide, n) {
        self._applyTransform(slide, n > target ? 100 : 0, false);
      });
      this._commitIndex(target);
      return;
    }

    if (target === from) return;

    var forward = target > from;
    var mover = forward ? this.slides[target] : this.slides[from];

    if (forward) {
      // Advance forward: animate mover up to viewport
      this._applyTransform(mover, 100, false);
      void mover.offsetHeight;
      this._applyTransform(mover, 0, true);
    } else {
      // Move backward: place lower slides and animate mover down
      this.slides.forEach(function (slide, n) {
        if (slide === mover) return;
        self._applyTransform(slide, n > target ? 100 : 0, false);
      });
      void this.root.offsetHeight;
      this._applyTransform(mover, 0, false);
      void mover.offsetHeight;
      this._applyTransform(mover, 100, true);
    }

    this._runTransition(mover, function () {
      // Reconcile remaining slides after transition
      self.slides.forEach(function (slide, n) {
        self._applyTransform(slide, n > target ? 100 : 0, false);
      });
    });

    this._commitIndex(target);
  };

  // Run card transition animation
  CardDeck.prototype._runTransition = function (mover, done) {
    var self = this;

    this._cancelTransition();

    this.animating = true;
    this.root.classList.add('is-animating');
    syncMotionFlag();
    mover.classList.add('is-moving');

    var finish = function () {
      if (self._transitionTimer) {
        clearTimeout(self._transitionTimer);
        self._transitionTimer = null;
      }
      mover.removeEventListener('transitionend', onEnd);
      mover.classList.remove('is-moving');
      self._active = null;
      if (self.destroyed) return;
      self.animating = false;
      self.root.classList.remove('is-animating');
      syncMotionFlag();
      if (done) done();
      self.root.dispatchEvent(new CustomEvent('deck:transitionend', {
        detail: { index: self.index },
        bubbles: true
      }));
    };

    var onEnd = function (e) {
      if (e.target !== mover || e.propertyName !== 'transform') return;
      finish();
    };

    this._active = { mover: mover, onEnd: onEnd };
    mover.addEventListener('transitionend', onEnd);
    this._transitionTimer = setTimeout(finish, DURATION + 80);
  };

  // Cancel in-flight transition
  CardDeck.prototype._cancelTransition = function () {
    if (this._transitionTimer) {
      clearTimeout(this._transitionTimer);
      this._transitionTimer = null;
    }
    var a = this._active;
    this._active = null;
    if (a) {
      a.mover.removeEventListener('transitionend', a.onEnd);
      a.mover.classList.remove('is-moving', 'is-dragging');
    }
    this.animating = false;
    this.root.classList.remove('is-animating');
    syncMotionFlag();
  };

  CardDeck.prototype.next = function () {
    if (this.isEnd()) return false;
    this.goTo(this.index + 1);
    return true;
  };

  CardDeck.prototype.prev = function () {
    if (this.isBeginning()) return false;
    this.goTo(this.index - 1);
    return true;
  };

  CardDeck.prototype.lock = function () { this.locked = true; };
  CardDeck.prototype.unlock = function () { this.locked = false; };

  CardDeck.prototype.update = function () {
    // Re-assert resting transforms, e.g. after a resize or an accordion
    // changing a slide's intrinsic height.
    var self = this;
    this.slides.forEach(function (slide, n) {
      if (self.animating) return;
      self._applyTransform(slide, n > self.index ? 100 : 0, false);
    });
  };

  /* ---------- inner scroll delegation ---------- */

  // Check if an inner element within the active slide can scroll in the requested direction
  CardDeck.prototype._innerScrollCanTake = function (target, direction) {
    var slide = this.slides[this.index];
    if (!slide || !target || !slide.contains(target)) return false;

    var node = target;
    while (node && node !== this.root) {
      if (node.nodeType === 1 && this._isScrollable(node)) {
        var max = node.scrollHeight - node.clientHeight;
        var top = node.scrollTop;
        if (direction > 0 && top < max - EDGE_EPSILON) return true;
        if (direction < 0 && top > EDGE_EPSILON) return true;
      }
      if (node === slide) break;
      node = node.parentNode;
    }
    return false;
  };

  CardDeck.prototype._isScrollable = function (el) {
    if (el.scrollHeight - el.clientHeight <= EDGE_EPSILON) return false;
    var overflowY = window.getComputedStyle(el).overflowY;
    return overflowY === 'auto' || overflowY === 'scroll';
  };

  // Viewport coverage and alignment
  // Return fraction of deck currently visible in viewport
  CardDeck.prototype._coverage = function () {
    var r = this.root.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    if (vh <= 0 || r.height <= 0) return 0;
    var visible = Math.min(r.bottom, vh) - Math.max(r.top, 0);
    if (visible <= 0) return 0;
    return visible / Math.min(r.height, vh);
  };

  // Check if deck covers enough viewport to capture gestures
  CardDeck.prototype._engaged = function () {
    return this._coverage() >= ENGAGE_RATIO;
  };

  // Check if deck top aligns with viewport top
  CardDeck.prototype._isAligned = function () {
    var top = this.root.getBoundingClientRect().top;
    return top <= ALIGN_SLACK && top >= -ALIGN_SLACK;
  };

  // Align deck top with viewport top
  CardDeck.prototype._alignTop = function () {
    var top = this.root.getBoundingClientRect().top +
      (window.pageYOffset || document.documentElement.scrollTop);
    try {
      window.scrollTo({ top: top, behavior: 'instant' });
    } catch (err) {
      window.scrollTo(0, top);
    }
  };

  // Seat stack to first or last slide based on entry direction
  CardDeck.prototype._seatForEntry = function () {
    if (this.animating) return;
    if (this._drag && this._drag.decided) return;
    var r = this.root.getBoundingClientRect();
    if (r.top >= -ALIGN_SLACK) return;
    var scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var deckAbsoluteTop = r.top + scrollY;
    if (scrollY < deckAbsoluteTop + (r.height * 0.5)) return;
    var last = this.slides.length - 1;
    if (this.index === last) return;
    this.goTo(last, { animate: false });
  };

  // Align deck if page scroll settled near alignment threshold
  CardDeck.prototype._maybeAlign = function () {
    if (this.destroyed || this.locked) return;
    if (this.animating || this._drag) return;
    if (!this._engaged()) { this._wasEngaged = false; return; }
    if (now() - this._releasedAt < RELEASE_GRACE) return;
    var fresh = !this._wasEngaged;
    this._wasEngaged = true;
    if (this._isAligned()) return;
    if (fresh) this._seatForEntry();
    this._alignTop();
  };

  // Event binding
  CardDeck.prototype._on = function (el, type, handler, opts) {
    el.addEventListener(type, handler, opts);
    this._boundHandlers.push([el, type, handler, opts]);
  };

  CardDeck.prototype._bind = function () {
    var self = this;

    // Wheel and trackpad gestures
    this._on(this.gestureRoot, 'wheel', function (e) {
      if (self.locked || self.destroyed) return;

      var delta = e.deltaY;
      if (Math.abs(delta) < WHEEL_DELTA_MIN) return;
      var direction = delta > 0 ? 1 : -1;

      // Yield to scrollable regions inside active slide
      if (self._innerScrollCanTake(e.target, direction)) return;

      // Yield if deck does not sufficiently cover viewport
      if (!self._engaged()) { self._wasEngaged = false; return; }

      // Seat stack on fresh entry before edge checks
      if (!self._wasEngaged) {
        self._wasEngaged = true;
        self._seatForEntry();
      }

      // Edge release: pass gesture to document
      if (direction > 0 && self.isEnd()) { self._releasedAt = now(); return; }
      if (direction < 0 && self.isBeginning()) { self._releasedAt = now(); return; }

      e.preventDefault();

      if (self.animating || self._wheelGestureActive) {
        if (self._wheelDebounceTimer) clearTimeout(self._wheelDebounceTimer);
        self._wheelDebounceTimer = setTimeout(function () {
          self._wheelGestureActive = false;
        }, 140);
        return;
      }

      if (!self._isAligned()) {
        self._alignTop();
        self._wheelGestureActive = true;
        if (self._wheelDebounceTimer) clearTimeout(self._wheelDebounceTimer);
        self._wheelDebounceTimer = setTimeout(function () {
          self._wheelGestureActive = false;
        }, WHEEL_TAIL);
        return;
      }

      self._wheelGestureActive = true;
      if (self._wheelDebounceTimer) clearTimeout(self._wheelDebounceTimer);
      self._wheelDebounceTimer = setTimeout(function () {
        self._wheelGestureActive = false;
      }, DURATION + WHEEL_TAIL);

      if (direction > 0) self.next();
      else self.prev();
    }, { passive: false });

    // Touch gestures

    this._on(this.gestureRoot, 'touchstart', function (e) {
      if (self.locked || self.destroyed || self.animating) return;
      if (e.touches.length !== 1) return;

      var touch = e.touches[0];
      self._drag = {
        startY: touch.clientY,
        startX: touch.clientX,
        lastY: touch.clientY,
        lastT: now(),
        velocity: 0,
        dy: 0,
        target: e.target,
        mover: null,
        forward: null,
        height: self.slideHeight(),
        decided: false,
        released: false
      };
    }, { passive: true });

    this._on(this.gestureRoot, 'touchmove', function (e) {
      var d = self._drag;
      if (!d || self.locked || self.destroyed) return;
      if (e.touches.length !== 1) return;

      var touch = e.touches[0];
      var dy = touch.clientY - d.startY;
      var dx = touch.clientX - d.startX;

      // Track velocity on every move so a flick is measurable on release.
      var t = now();
      var dt = t - d.lastT;
      if (dt > 0) {
        d.velocity = (touch.clientY - d.lastY) / dt;
        d.lastY = touch.clientY;
        d.lastT = t;
      }
      d.dy = dy;

      if (!d.decided) {
        // Ignore until the gesture is clearly vertical.
        if (Math.abs(dy) < 6) return;
        if (Math.abs(dx) > Math.abs(dy)) {
          self._drag = null;
          return;
        }

        var direction = dy < 0 ? 1 : -1;

        if (self._innerScrollCanTake(d.target, direction)) {
          self._drag = null;
          return;
        }

        if (!self._engaged()) {
          self._wasEngaged = false;
          self._drag = null;
          return;
        }

        if (!self._wasEngaged) {
          self._wasEngaged = true;
          self._seatForEntry();
        }

        // Edge release: pass drag to document
        if (direction > 0 && self.isEnd()) { self._releasedAt = now(); self._drag = null; return; }
        if (direction < 0 && self.isBeginning()) { self._releasedAt = now(); self._drag = null; return; }

        d.forward = direction > 0;
        d.mover = d.forward ? self.slides[self.index + 1] : self.slides[self.index];
        d.decided = true;
        self.root.classList.add('is-dragging');
        syncMotionFlag();
        if (d.mover) {
          d.mover.classList.add('is-dragging', 'is-moving');
        }

        self.root.dispatchEvent(new CustomEvent('deck:dragstart', {
          detail: {
            index: self.index,
            targetIndex: d.forward ? self.index + 1 : self.index - 1,
            forward: d.forward,
            slide: d.mover
          },
          bubbles: true
        }));
      }

      if (!d.mover) return;

      e.preventDefault();

      if (d.forward) {
        self._applyTransformPx(d.mover, clamp(d.height + dy, 0, d.height));
      } else {
        self._applyTransformPx(d.mover, clamp(dy, 0, d.height));
      }
    }, { passive: false });

    var endDrag = function () {
      var d = self._drag;
      self._drag = null;
      if (!d || !d.decided || !d.mover || d.released) return;
      d.released = true;

      self.root.classList.remove('is-dragging');
      d.mover.classList.remove('is-dragging');

      var travelled = Math.abs(d.dy);
      var passedThreshold = travelled > d.height * DRAG_COMMIT_RATIO;
      var flicked = Math.abs(d.velocity) > FLICK_VELOCITY &&
        ((d.forward && d.velocity < 0) || (!d.forward && d.velocity > 0));

      var commit = passedThreshold || flicked;
      var target = commit
        ? clamp(d.forward ? self.index + 1 : self.index - 1, 0, self.slides.length - 1)
        : self.index;

      if (reduceMotion.matches) {
        self._applyTransform(d.mover, self.slides.indexOf(d.mover) > target ? 100 : 0, false);
        d.mover.classList.remove('is-moving');
        if (commit) self._commitIndex(target);
      } else if (commit) {
        self._applyTransform(d.mover, d.forward ? 0 : 100, true);
        self._runTransition(d.mover, function () {
          var moverIdx = self.slides.indexOf(d.mover);
          self._applyTransform(d.mover, moverIdx > target ? 100 : 0, false);
        });
        self._commitIndex(target);
      } else {
        self._applyTransform(d.mover, d.forward ? 100 : 0, true);
        self._runTransition(d.mover, function () {
          var moverIdx = self.slides.indexOf(d.mover);
          self._applyTransform(d.mover, moverIdx > self.index ? 100 : 0, false);
        });
      }

      self.root.dispatchEvent(new CustomEvent('deck:dragend', {
        detail: { index: self.index, committed: commit },
        bubbles: true
      }));

      syncMotionFlag();
    };

    this._on(this.gestureRoot, 'touchend', endDrag, { passive: true });
    this._on(this.gestureRoot, 'touchcancel', endDrag, { passive: true });

    // Keyboard navigation
    this._on(this.gestureRoot, 'keydown', function (e) {
      if (self.locked || self.destroyed) return;
      var tag = e.target && e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
          (e.target && e.target.isContentEditable)) return;

      var handled = false;
      switch (e.key) {
        case 'ArrowDown':
        case 'PageDown':
          if (!self.isEnd()) handled = self.next();
          break;
        case 'ArrowUp':
        case 'PageUp':
          if (!self.isBeginning()) handled = self.prev();
          break;
        case 'Home':
          if (!self.isBeginning()) { self.goTo(0); handled = true; }
          break;
        case 'End':
          if (!self.isEnd()) { self.goTo(self.slides.length - 1); handled = true; }
          break;
      }
      if (handled) e.preventDefault();
    }, { passive: false });

    // Window resize handling
    var resizeTimer = null;
    this._onResize = function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { self.update(); }, 120);
    };
    this._on(window, 'resize', this._onResize, { passive: true });
    this._on(window, 'orientationchange', this._onResize, { passive: true });

    // Scroll settle alignment
    var settleTimer = null;
    this._onScroll = function () {
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(function () { self._maybeAlign(); }, SCROLL_SETTLE);
    };
    this._on(window, 'scroll', this._onScroll, { passive: true });
  };

  // Cleanup and destroy
  CardDeck.prototype.destroy = function () {
    this.destroyed = true;
    this._cancelTransition();
    if (this._wheelDebounceTimer) {
      clearTimeout(this._wheelDebounceTimer);
      this._wheelDebounceTimer = null;
    }
    this._boundHandlers.forEach(function (entry) {
      entry[0].removeEventListener(entry[1], entry[2], entry[3]);
    });
    this._boundHandlers = [];
    this.slides.forEach(function (slide) {
      slide.style.transition = '';
      slide.style.transform = '';
      slide.style.zIndex = '';
      slide.classList.remove('deck__slide', 'is-active', 'is-stacked', 'is-parked', 'is-dragging', 'is-moving');
      slide.removeAttribute('aria-hidden');
    });
    this.root.classList.remove('deck', 'is-animating', 'is-dragging', 'is-first', 'is-last');
    if (!document.querySelector('.deck')) {
      document.documentElement.classList.remove('has-card-deck');
    }
    syncMotionFlag();
  };

  window.CardDeck = CardDeck;
})();
